/**
 * System Health tab — read-only ops dashboard.
 *
 * Reads `GET /v1/admin/system-health` (T2 PR-1 #1347) which returns:
 *
 *   {
 *     generated_at: string,
 *     alarms: { status, error, truncated, counts, in_alarm },
 *       counts also carries alarm_routed_standalone / alarm_unrouted /
 *       alarm_constituent — the ALARM count alone is not a to-do list:
 *       constituents belong to their parent, unrouted alarms told nobody.
 *     errors: { status, error, last_1h, last_24h, top_error_codes_24h },
 *     lambdas: Array<{ name, invocations_1h, errors_1h, avg_duration_ms,
 *                      status, error }>,
 *     outbox: { status, error, pending, skipped_24h, sent_24h,
 *               oldest_pending_age_seconds },
 *     migrations: { status, error, latest_applied, failed_count_7d },
 *     push_delivery: { status, error, sent_24h, failed_24h, failure_rate_pct },
 *   }
 *
 * Every tile carries {status, error}. status 'unavailable' means the read
 * failed — the card must show an em-dash, never a zero (U29, 2026-08-21).
 *
 * 30s auto-refresh (matches the backend's 30s server-side cache TTL).
 * Read-only — no actions, no audit row.
 *
 * Spec: docs/superpowers/specs/2026-05-28-admin-analytics-tabs-design.md §8.
 * Plan: docs/superpowers/plans/2026-05-28-admin-analytics-tabs.md Task 14.
 */
import { useCallback, useEffect, useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { RefreshCw } from 'lucide-react';
import { adminFetch, AdminApiError } from '@/providers/admin-api';
import { ActiveAlarmsCard } from './components/ActiveAlarmsCard';
import { ErrorRateCard } from './components/ErrorRateCard';
import { LambdaHealthCard } from './components/LambdaHealthCard';
import { OutboxCard } from './components/OutboxCard';
import { MigrationsCard } from './components/MigrationsCard';
import { PushDeliveryCard } from './components/PushDeliveryCard';

interface SystemHealthResponse {
  generated_at: string;
  alarms: any;
  errors: any;
  lambdas: any[];
  outbox: any;
  migrations: any;
  push_delivery: any;
}

export function SystemHealthPage() {
  const apiUrl = useApiUrl();
  const [data, setData] = useState<SystemHealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(
    async (showSkeleton: boolean) => {
      if (showSkeleton) setLoading(true);
      else setRefreshing(true);
      setError(null);
      try {
        const res = await adminFetch(`${apiUrl}/v1/admin/system-health`);
        const d: SystemHealthResponse = await res.json();
        setData(d);
      } catch (e) {
        const msg =
          e instanceof AdminApiError
            ? `Failed to load system health (HTTP ${e.status}): ${e.message}`
            : (e as Error)?.message || 'Failed to load system health';
        setError(msg);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [apiUrl],
  );

  useEffect(() => {
    void load(true);
    const interval = setInterval(() => void load(false), 30_000);
    return () => clearInterval(interval);
  }, [load]);

  if (error && !data) {
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold">System Health</h2>
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-destructive">
          {error}
          <button onClick={() => void load(true)} className="ml-4 underline">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold">System Health</h2>
        <p className="text-sm text-muted-foreground">Loading system health…</p>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold">System Health</h2>
          <p className="text-muted-foreground">
            CloudWatch alarms + error rates + critical Lambdas + outbox + migrations + push.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {data.generated_at && (
            <span className="text-xs text-muted-foreground">
              Last updated {new Date(data.generated_at).toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={() => void load(false)}
            disabled={refreshing}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            aria-label="Refresh system health"
          >
            <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-3 text-sm text-yellow-700">
          Refresh failed: {error} (showing previous values)
        </div>
      )}

      <ActiveAlarmsCard data={data.alarms} />
      <ErrorRateCard data={data.errors} />
      <LambdaHealthCard data={data.lambdas} />
      <OutboxCard data={data.outbox} />
      <MigrationsCard data={data.migrations} />
      <PushDeliveryCard data={data.push_delivery} />
    </div>
  );
}
